import type { NormalizedDreamInput, NormalizedConversation, ConversationRow, PkWatermark } from "../types.js";
import type { AdvancePlan } from "../types.js";
import { computeWatermark, justBefore } from "../cursor.js";

/** Hard per-cycle input bound (DESIGN §4.1, folds F13). "Fits one context" is a
 *  TODAY claim — a never-dreamed worker / --reset / post-downtime backlog can
 *  harvest the whole window, so cap and process the N-oldest, advancing the
 *  cursor only over what we consumed. */
export const DEFAULT_CORPUS_CAP = 40;

/**
 * Cap the dream corpus to the N-OLDEST-unconsumed conversations. Returns the
 * capped input (with `advance` narrowed to ONLY the kept rows → PARTIAL cursor
 * advance) and the deferred remainder (for the digest's deferred note, Task 18).
 * Under the cap, the input passes through unchanged.
 *
 * The partial advance is RE-DERIVED (not filtered): the kept set is the N-OLDEST
 * while the deferred set is the NEWEST, so the full-corpus `ts` (≈ max consumed
 * emit-time) reflects DEFERRED rows. Reusing it with a filtered key list would
 * leave `ts`/`consumedRowKeys` inconsistent with what `computeWatermark` derives
 * for the kept set — deferred rows in the overlap floor could then be silently
 * skipped next run. Instead we re-run the SAME watermark math over the kept rows'
 * provenance (rebuilt per physical PK), then re-apply buildAdvancePlan's monotonic
 * re-pin (priorTsByPk) and fetch-error fence (failedTsByPk). DESIGN §3 / §4.1.
 */
export function capCorpus(
  input: NormalizedDreamInput,
  cap: number = DEFAULT_CORPUS_CAP,
): { capped: NormalizedDreamInput; deferred: NormalizedConversation[] } {
  const ordered = [...input.conversations].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  if (ordered.length <= cap) {
    return { capped: { ...input, conversations: ordered }, deferred: [] };
  }
  const kept = ordered.slice(0, cap);
  const deferred = ordered.slice(cap);

  const advance = narrowAdvance(input.advance, kept);
  return { capped: { ...input, conversations: kept, advance }, deferred };
}

/**
 * Re-derive the AdvancePlan over ONLY the kept conversations' source rows. Groups
 * the kept rows back under their physical PK (from `provenance`) and recomputes
 * each PK's watermark with the SAME `computeWatermark` + re-pin + fence the
 * pipeline used — so the partial `ts` and `consumedRowKeys` are mutually
 * consistent (the doc-comment's "overlap-dedup supports incremental progress"
 * actually holds). Falls back to a membership filter only if the watermark inputs
 * were not carried (a hand-built plan) — production always carries them.
 */
function narrowAdvance(src: AdvancePlan, kept: NormalizedConversation[]): AdvancePlan {
  // Rebuild the kept rows per physical PK from each conversation's provenance.
  const keptRowsByPk: Record<string, ConversationRow[]> = {};
  for (const c of kept) {
    const prov = c.provenance;
    if (!prov) continue;
    (keptRowsByPk[prov.pk] ??= []).push(...prov.rows);
  }

  // Without the watermark inputs we cannot re-derive soundly; degrade to filtering
  // the full-corpus advance down to the kept rows (keeps a hand-built plan working).
  if (src.now === undefined || src.safetyLagSeconds === undefined) {
    return filterAdvance(src, keptRowsByPk);
  }
  const now = src.now;
  const safetyLagSeconds = src.safetyLagSeconds;
  const priorTsByPk = src.priorTsByPk ?? {};

  const watermarks: Record<string, PkWatermark> = {};
  const consumedRowsByPk: Record<string, string[]> = {};
  for (const pk of Object.keys(src.watermarks)) {
    const rows = keptRowsByPk[pk] ?? [];
    // SAME per-PK math as pipeline.buildAdvancePlan: watermark → re-pin → fence.
    const { ts, consumedRowKeys } = computeWatermark(rows, now, safetyLagSeconds);
    let candidate = ts;
    const prior = priorTsByPk[pk];
    if (prior && candidate < prior) candidate = prior;
    const failed = src.failedTsByPk[pk];
    if (failed && candidate >= failed) candidate = justBefore(failed);
    watermarks[pk] = { ts: candidate, consumedRowKeys };
    consumedRowsByPk[pk] = consumedRowKeys;
  }
  return {
    worker: src.worker, watermarks, consumedRowsByPk, failedTsByPk: src.failedTsByPk,
    now, safetyLagSeconds, priorTsByPk,
  };
}

/** Degraded narrowing for a hand-built plan with no watermark inputs: keep only
 *  the kept rows' RowKeys, reusing the full-corpus ts (the historical behavior). */
function filterAdvance(src: AdvancePlan, keptRowsByPk: Record<string, ConversationRow[]>): AdvancePlan {
  const keepKeys: Record<string, Set<string>> = {};
  for (const [pk, rows] of Object.entries(keptRowsByPk)) {
    keepKeys[pk] = new Set(rows.map((r) => r.rowKey));
  }
  const watermarks: AdvancePlan["watermarks"] = {};
  const consumedRowsByPk: Record<string, string[]> = {};
  for (const [pk, wm] of Object.entries(src.watermarks)) {
    const keep = keepKeys[pk] ?? new Set<string>();
    consumedRowsByPk[pk] = (src.consumedRowsByPk[pk] ?? []).filter((rk) => keep.has(rk));
    watermarks[pk] = { ts: wm.ts, consumedRowKeys: wm.consumedRowKeys.filter((rk) => keep.has(rk)) };
  }
  return { worker: src.worker, watermarks, consumedRowsByPk, failedTsByPk: src.failedTsByPk };
}
